import React, { useState, useEffect } from 'react';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Form, Modal, Button } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';
import { toast } from 'react-toastify';
import { getPlayerPokemons } from '../../store/pokemon';
import '../../assets/css/pokemonMarket.css';

function PockemonMarket() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const userData = useSelector(state => state.auth.user_data);
    const player_pokemons = useSelector(state => state.pokemon.player_pokemons || []);
    const [activeTab, setActiveTab] = useState('direct');
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSellModal, setShowSellModal] = useState(false);
    const [sellForm, setSellForm] = useState({
        pokemon_id: '',
        sale_type: 'direct',
        currency_type: 'silver',
        price: '',
        buyer_id: ''
    });
    const [selling, setSelling] = useState(false);
    const [showBidModal, setShowBidModal] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [bidAmount, setBidAmount] = useState('');
    const [bidding, setBidding] = useState(false);
    const [playerSearch, setPlayerSearch] = useState('');
    const [playerSearchResults, setPlayerSearchResults] = useState([]);
    const [searchingPlayers, setSearchingPlayers] = useState(false);
    const [filters, setFilters] = useState({
        species: '',
        min_power: '',
        max_power: '',
        region: '',
        price_range: '',
        trainer: '',
        level_range: '',
        equipped: '',
        shiny_only: false,
        show_my_pokemon_only: false
    });

    useEffect(() => {
        fetchListings();
        // Load player pokemons for sell modal
        dispatch(getPlayerPokemons());
    }, [activeTab, filters, dispatch]);

    const fetchListings = async () => {
        try {
            setLoading(true);
            // Map tab names to database sale_type values
            const saleTypeMap = {
                'auctions': 'auction',
                'direct': 'direct',
                'private': 'private'
            };
            const dbSaleType = saleTypeMap[activeTab] || activeTab;
            
            const params = new URLSearchParams({
                sale_type: dbSaleType,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== '' && v !== false)
                )
            });
            
            console.log('Fetching listings with params:', params.toString());
            const response = await axiosInstance.get(`${API_ENDPOINTS.GET_MARKET_LISTINGS}?${params}`);
            console.log('Listings response:', response.data);
            console.log('Listings response debug:', response.data?.debug);
            
            if (response.data && response.data.success) {
                console.log('Listings count:', response.data.listings?.length || 0);
                console.log('Listings array:', response.data.listings);
                setListings(response.data.listings || []);
            } else {
                console.warn('Listings response not successful:', response.data);
                setListings([]);
            }
        } catch (error) {
            console.error('Error fetching listings:', error);
            console.error('Error response:', error.response?.data);
            // Don't show error toast if table doesn't exist yet - it's expected
            if (error.response?.status !== 500) {
                toast.error('Failed to load market listings');
            }
            setListings([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchListings();
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleBuy = async (listing) => {
        if (!listing || !listing.id) {
            toast.error(t('Invalid listing'));
            return;
        }
        
        // Log the listing for debugging
        console.log('Buy Pokemon - Listing data:', {
            id: listing.id,
            pokemon_name: listing.pokemon_name,
            price: listing.price,
            currency_type: listing.currency_type,
            status: listing.status,
            expires_at: listing.expires_at
        });
        
        // Confirm purchase
        if (!window.confirm(t('Are you sure you want to buy this Pokemon for') + ' ' + listing.price.toLocaleString() + ' ' + (listing.currency_type === 'gold' ? t('gold') : t('silver')) + '?')) {
            return;
        }
        
        try {
            const requestData = {
                sale_id: listing.id
            };
            console.log('Buy Pokemon - Sending request:', requestData);
            
            const response = await axiosInstance.post(API_ENDPOINTS.BUY_POKEMON, requestData);
            
            console.log('Buy Pokemon - Response:', response.data);
            
            if (response.data && response.data.success) {
                toast.success(t(response.data.message || 'Pokemon purchased successfully!'));
                // Refresh listings to remove the sold Pokemon
                fetchListings();
                // Refresh player pokemons to show the new Pokemon
                dispatch(getPlayerPokemons());
            } else {
                toast.error(t(response.data.error || 'Failed to purchase Pokemon'));
            }
        } catch (error) {
            console.error('Error buying Pokemon:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to purchase Pokemon';
            toast.error(t(errorMessage));
        }
    };

    const handleOpenSellModal = () => {
        // Map active tab to sale_type
        const tabToSaleTypeMap = {
            'direct': 'direct',
            'auctions': 'auction',
            'private': 'private'
        };
        const defaultSaleType = tabToSaleTypeMap[activeTab] || 'direct';
        
        setSellForm({
            pokemon_id: '',
            sale_type: defaultSaleType,
            currency_type: 'silver',
            price: '',
            buyer_id: ''
        });
        setPlayerSearch('');
        setPlayerSearchResults([]);
        setShowSellModal(true);
    };

    const handleCloseSellModal = () => {
        setShowSellModal(false);
        setSellForm({
            pokemon_id: '',
            sale_type: 'direct',
            currency_type: 'silver',
            price: '',
            buyer_id: ''
        });
        setPlayerSearch('');
        setPlayerSearchResults([]);
    };

    const handleSellFormChange = (field, value) => {
        setSellForm(prev => ({
            ...prev,
            [field]: value
        }));
        
        // When sale_type changes, also change the active tab to match
        if (field === 'sale_type') {
            const tabMap = {
                'direct': 'direct',
                'auction': 'auctions',
                'private': 'private'
            };
            if (tabMap[value]) {
                setActiveTab(tabMap[value]);
            }
        }
    };

    const handleSellPokemon = async (e) => {
        e.preventDefault();
        
        if (!sellForm.pokemon_id || !sellForm.price || sellForm.price <= 0) {
            toast.error(t('Please fill all required fields'));
            return;
        }
        
        // For private sales, buyer_id is required
        if (sellForm.sale_type === 'private' && !sellForm.buyer_id) {
            toast.error(t('Please select a buyer for private sale'));
            return;
        }

        setSelling(true);
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.SELL_POKEMON, sellForm);
            
            if (response.data && response.data.success) {
                toast.success(response.data.message || t('Pokemon listed for sale successfully!'));
                handleCloseSellModal();
                fetchListings(); // Refresh listings
                dispatch(getPlayerPokemons()); // Refresh player pokemons
            } else {
                toast.error(response.data.error || t('Failed to list Pokemon for sale'));
            }
        } catch (error) {
            console.error('Error selling Pokemon:', error);
            toast.error(error.response?.data?.error || t('Failed to list Pokemon for sale'));
        } finally {
            setSelling(false);
        }
    };
    
    const handleSearchPlayers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setPlayerSearchResults([]);
            return;
        }
        
        setSearchingPlayers(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.GET_PLAYERS}?player=${encodeURIComponent(searchTerm)}`);
            if (response.data && response.data.success && response.data.data) {
                setPlayerSearchResults(response.data.data);
            } else {
                setPlayerSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching players:', error);
            setPlayerSearchResults([]);
        } finally {
            setSearchingPlayers(false);
        }
    };
    
    const handlePlayerSearchChange = (e) => {
        const value = e.target.value;
        setPlayerSearch(value);
        handleSearchPlayers(value);
    };
    
    const handleSelectPlayer = (player) => {
        setSellForm(prev => ({
            ...prev,
            buyer_id: player.user_id
        }));
        setPlayerSearch(player.username);
        setPlayerSearchResults([]);
    };
    
    const handleOpenBidModal = (listing) => {
        setSelectedListing(listing);
        setBidAmount(listing.price + 1); // Set minimum bid to current price + 1
        setShowBidModal(true);
    };
    
    const handleCloseBidModal = () => {
        setShowBidModal(false);
        setSelectedListing(null);
        setBidAmount('');
    };
    
    const handleBid = async (e) => {
        e.preventDefault();
        
        if (!selectedListing || !bidAmount || bidAmount <= 0) {
            toast.error(t('Please enter a valid bid amount'));
            return;
        }
        
        // Check if bid is higher than current price
        if (parseInt(bidAmount) <= selectedListing.price) {
            toast.error(t('Bid must be higher than current price of') + ' ' + selectedListing.price);
            return;
        }
        
        setBidding(true);
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.BID_ON_AUCTION, {
                sale_id: selectedListing.id,
                bid_amount: parseInt(bidAmount)
            });
            
            if (response.data && response.data.success) {
                toast.success(response.data.message || t('Bid placed successfully!'));
                handleCloseBidModal();
                fetchListings(); // Refresh listings
            } else {
                toast.error(response.data.error || t('Failed to place bid'));
            }
        } catch (error) {
            console.error('Error placing bid:', error);
            toast.error(error.response?.data?.error || t('Failed to place bid'));
        } finally {
            setBidding(false);
        }
    };

    // Get available Pokemon for sale (all Pokemon EXCEPT those in active team - opzak !== 'ja')
    const availablePokemons = player_pokemons.filter(p => p.opzak !== 'ja');

    return (
        <div className="ar_mainBody">
            <GoldSiverHeader previous={'/home'} title={t('Market')}>
                {/* Filter Area */}
                <section className="ar_market_filter_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_filter_title">
                                    <div className="ar_market_cover_image_wrapper">
                                        <img src="/images/cover/1.png" alt="Market Cover" className="ar_market_cover_image" />
                                    </div>
                                    <div className="ar_market_cover_overlay"></div>
                                    <div className="ar_market_cover_content">
                                        <h1>{t('Pokemon Market')}</h1>
                                       
                                        <p>{t('Buy and sell Pokémon at the best price with our sales methods!')}</p>
                                    </div>
                                </div>
                                <div className="ar_market_filter_area">
                                    <h4>{t('Filter Sales')}</h4>
                                    <form className="ar_market_filter_form" onSubmit={handleSearch}>
                                        <Row>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Species')}:</label>
                                                    <Form.Select 
                                                        value={filters.species}
                                                        onChange={(e) => handleFilterChange('species', e.target.value)}
                                                    >
                                                        <option value="">{t('None')}</option>
                                                        <option>Pikachu</option>
                                                        <option>Charizard</option>
                                                        <option>Mewtwo</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Full power')}:</label>
                                                    <Form.Control 
                                                        type="number" 
                                                        placeholder=""
                                                        value={filters.min_power}
                                                        onChange={(e) => handleFilterChange('min_power', e.target.value)}
                                                    />
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Region')}:</label>
                                                    <Form.Select 
                                                        value={filters.region}
                                                        onChange={(e) => handleFilterChange('region', e.target.value)}
                                                    >
                                                        <option value="">{t('None')}</option>
                                                        <option>Kanto</option>
                                                        <option>Johto</option>
                                                        <option>Hoenn</option>
                                                        <option>Sinnoh</option>
                                                        <option>Unova</option>
                                                        <option>Kalos</option>
                                                        <option>Alola</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Price')}:</label>
                                                    <Form.Select 
                                                        value={filters.price_range}
                                                        onChange={(e) => handleFilterChange('price_range', e.target.value)}
                                                    >
                                                        <option value="">None</option>
                                                        <option value="0-1000">0-1000</option>
                                                        <option value="1000-5000">1000-5000</option>
                                                        <option value="5000+">5000+</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Trainer')}:</label>
                                                    <Form.Control 
                                                        type="text" 
                                                        placeholder=""
                                                        value={filters.trainer}
                                                        onChange={(e) => handleFilterChange('trainer', e.target.value)}
                                                    />
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Level')}:</label>
                                                    <Form.Select 
                                                        value={filters.level_range}
                                                        onChange={(e) => handleFilterChange('level_range', e.target.value)}
                                                    >
                                                        <option value="">{t('None')}</option>
                                                        <option value="1-10">1-10</option>
                                                        <option value="10-50">10-50</option>
                                                        <option value="50-100">50-100</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label>{t('Equipped')}:</label>
                                                    <Form.Select 
                                                        value={filters.equipped}
                                                        onChange={(e) => handleFilterChange('equipped', e.target.value)}
                                                    >
                                                        <option value="">{t('None')}</option>
                                                        <option value="yes">{t('Yes')}</option>
                                                        <option value="no">{t('No')}</option>
                                                    </Form.Select>
                                                </div>
                                            </Col>
                                            <Col md={3} sm={6}>
                                                <div className="ar_market_filter_field">
                                                    <label className="ar_market_checkbox_label">
                                                        <Form.Check 
                                                            type="checkbox"
                                                            checked={filters.shiny_only}
                                                            onChange={(e) => handleFilterChange('shiny_only', e.target.checked)}
                                                        />
                                                        {t('Just Shiny')}
                                                    </label>
                                                </div>
                                            </Col>
                                        </Row>
                                        <div className="ar_market_filter_actions">
                                            <button type="submit" className="ar_market_search_btn">{t('Search')}</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Market Tabs Area */}
                <section className="ar_market_tabs_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_tabs_area">
                                    <ul className="ar_market_tabs_list">
                                        <li className={activeTab === 'auctions' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); handleTabChange('auctions'); }} data-tab="auctions">
                                                {t('Auctions')}
                                            </a>
                                        </li>
                                        <li className={activeTab === 'direct' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); handleTabChange('direct'); }} data-tab="direct">
                                                {t('Direct')}
                                            </a>
                                        </li>
                                        <li className={activeTab === 'private' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); handleTabChange('private'); }} data-tab="private">
                                                {t('Private')}
                                            </a>
                                        </li>
                                    </ul>
                                    <div className="ar_market_show_my_pokemon">
                                        <label className="ar_market_checkbox_label">
                                            <Form.Check 
                                                type="checkbox"
                                                checked={filters.show_my_pokemon_only}
                                                onChange={(e) => handleFilterChange('show_my_pokemon_only', e.target.checked)}
                                            />
                                            {t('Show only my Pokémon')}
                                        </label>
                                    </div>
                                    <div className="ar_market_sell_button">
                                        <Button 
                                            className="ar_market_search_btn"
                                            onClick={handleOpenSellModal}
                                            style={{ margin: 0 }}
                                        >
                                            {t('Sell Pokemon')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Market Table Area */}
                <section className="ar_market_table_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_table_area">
                                    <div className="ar_market_table_wrapper">
                                        <table className="ar_market_table">
                                            <thead>
                                                <tr>
                                                    <th>{t('Pokemon')}</th>
                                                    <th>{t('Features')}</th>
                                                    <th>{t('Total Power')}</th>
                                                    <th>{t('Date')}</th>
                                                    <th>{t('Price')}</th>
                                                    <th>{t('Action')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="6" className="ar_market_no_data">{t('Loading...')}</td>
                                                    </tr>
                                                ) : listings.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="ar_market_no_data">{t('No listings available')}</td>
                                                    </tr>
                                                ) : (
                                                    listings.map((listing) => (
                                                        <tr key={listing.id} className="ar_market_table_row">
                                                            <td>
                                                                <div className="ar_market_pokemon_cell">
                                                                    <img 
                                                                        src={`/images/pokemon/${listing.pokemon_id_wild}.gif`} 
                                                                        alt={listing.pokemon_name} 
                                                                        className="ar_market_pokemon_img"
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="ar_market_features_cell">
                                                                    {listing.type1 && (
                                                                        <span className="ar_market_feature_item">⚡ {listing.type1}</span>
                                                                    )}
                                                                    <span className="ar_market_feature_item">⭐ {t('Level')} {listing.level}</span>
                                                                    {listing.shiny && (
                                                                        <span className="ar_market_feature_item">✨ {t('Shiny')}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="ar_market_power_cell">
                                                                    <strong>{listing.total_power.toLocaleString()}</strong>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="ar_market_date_cell">
                                                                    {new Date(listing.created_at).toLocaleDateString()}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="ar_market_price_cell">
                                                                    <img 
                                                                        src={listing.currency_type === 'gold' ? '/images/mock-04.png' : '/images/mock-05.png'} 
                                                                        alt={listing.currency_type} 
                                                                        className="ar_market_price_icon"
                                                                    />
                                                                    <strong>{listing.price.toLocaleString()}</strong>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="ar_market_action_buttons">
                                                                    {listing.sale_type === 'auction' ? (
                                                                        <button 
                                                                            className="ar_market_bid_btn"
                                                                            onClick={() => handleOpenBidModal(listing)}
                                                                        >
                                                                            {t('Bid')}
                                                                        </button>
                                                                    ) : (
                                                                        <button 
                                                                            className="ar_market_buy_btn"
                                                                            onClick={() => handleBuy(listing)}
                                                                        >
                                                                            {t('Buy')}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </GoldSiverHeader>

            {/* Sell Pokemon Modal */}
            <Modal 
                show={showSellModal} 
                onHide={handleCloseSellModal}
                size="lg"
                centered
                backdrop="static"
            >
                <Modal.Header className="bg-theme text-white" style={{ position: 'relative' }}>
                    <Modal.Title>{t('Sell Pokemon')}</Modal.Title>
                    {/* Close Button with Image - inside header */}
                    <a 
                        href="#" 
                        className="modal_close"
                        onClick={(e) => {
                            e.preventDefault();
                            handleCloseSellModal();
                        }}
                        style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            zIndex: 10000
                        }}
                    >
                        <img src="/images/menu/popup-close-button.png" alt="Close" style={{ width: '35px', height: '35px' }} />
                    </a>
                </Modal.Header>
                <Modal.Body className="bg-theme text-white">
                    <Form onSubmit={handleSellPokemon}>
                        <Row>
                            <Col md={12} className="mb-3">
                                <Form.Label>{t('Select Pokemon')}:</Form.Label>
                                <Form.Select
                                    value={sellForm.pokemon_id}
                                    onChange={(e) => handleSellFormChange('pokemon_id', e.target.value)}
                                    required
                                >
                                    <option value="">{t('Choose a Pokemon...')}</option>
                                    {availablePokemons.map((pokemon) => (
                                        <option key={pokemon.id} value={pokemon.id}>
                                            {pokemon.naam} - Level {pokemon.level} {pokemon.shiny ? '✨' : ''}
                                        </option>
                                    ))}
                                </Form.Select>
                                {availablePokemons.length === 0 && (
                                    <small className="text-warning">{t('No Pokemon available for sale (all Pokemon are in your active team)')}</small>
                                )}
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label>{t('Sale Type')}:</Form.Label>
                                <Form.Select
                                    value={sellForm.sale_type}
                                    onChange={(e) => handleSellFormChange('sale_type', e.target.value)}
                                    required
                                >
                                    <option value="direct">{t('Direct Sale')}</option>
                                    <option value="auction">{t('Auction')}</option>
                                    <option value="private">{t('Private Sale')}</option>
                                </Form.Select>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label>{t('Currency')}:</Form.Label>
                                <Form.Select
                                    value={sellForm.currency_type}
                                    onChange={(e) => handleSellFormChange('currency_type', e.target.value)}
                                    required
                                >
                                    <option value="silver">{t('silver')}</option>
                                    <option value="gold">{t('Gold (Requires Admin Approval)')}</option>
                                </Form.Select>
                            </Col>
                            <Col md={12} className="mb-3">
                                <Form.Label>{t('Price')}:</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    value={sellForm.price}
                                    onChange={(e) => handleSellFormChange('price', e.target.value)}
                                    placeholder={t('Enter price')}
                                    required
                                />
                                <small className="text-muted">
                                    {t('Commission')}: 3% ({(sellForm.price * 0.03).toFixed(2)} {sellForm.currency_type === 'gold' ? t('gold') : t('silver')})
                                </small>
                            </Col>
                            {sellForm.sale_type === 'private' && (
                                <Col md={12} className="mb-3">
                                    <Form.Label>{t('Select Buyer')}:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={playerSearch}
                                        onChange={handlePlayerSearchChange}
                                        placeholder={t('Type player name to search...')}
                                        required
                                    />
                                    {playerSearchResults.length > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            zIndex: 1000,
                                            backgroundColor: '#fff',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            width: '100%',
                                            marginTop: '5px'
                                        }}>
                                            {playerSearchResults.map((player) => (
                                                <div
                                                    key={player.user_id}
                                                    onClick={() => handleSelectPlayer(player)}
                                                    style={{
                                                        padding: '10px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #eee'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#091647'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                                                >
                                                    {player.username}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {sellForm.buyer_id && (
                                        <small className="text-success">{t('Buyer selected')}</small>
                                    )}
                                </Col>
                            )}
                            {sellForm.pokemon_id && (
                                <Col md={12} className="mb-3">
                                    <div className="text-center">
                                        <img 
                                            src={`/images/pokemon/${availablePokemons.find(p => p.id === parseInt(sellForm.pokemon_id))?.wild_id || 1}.gif`}
                                            alt="Selected Pokemon"
                                            style={{ maxWidth: '150px' }}
                                        />
                                    </div>
                                </Col>
                            )}
                        </Row>
                        <div className="text-center mt-3">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={selling || !sellForm.pokemon_id || !sellForm.price || (sellForm.sale_type === 'private' && !sellForm.buyer_id)}
                                className="ar_market_search_btn"
                            >
                                {selling ? t('Listing...') : t('List for Sale')}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Bid Modal */}
            <Modal 
                show={showBidModal} 
                onHide={handleCloseBidModal}
                size="md"
                centered
                backdrop="static"
            >
                <Modal.Header className="bg-theme text-white" style={{ position: 'relative' }}>
                    <Modal.Title>{t('Place Bid')}</Modal.Title>
                    <a 
                        href="#" 
                        className="modal_close"
                        onClick={(e) => {
                            e.preventDefault();
                            handleCloseBidModal();
                        }}
                        style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            zIndex: 10000
                        }}
                    >
                        <img src="/images/menu/popup-close-button.png" alt="Close" style={{ width: '35px', height: '35px' }} />
                    </a>
                </Modal.Header>
                <Modal.Body className="bg-theme text-white">
                    {selectedListing && (
                        <Form onSubmit={handleBid}>
                            <div className="mb-3">
                                <p><strong>{t('Pokemon')}:</strong> {selectedListing.pokemon_name}</p>
                                <p><strong>{t('Current Price')}:</strong> {selectedListing.price.toLocaleString()} {selectedListing.currency_type === 'gold' ? t('gold') : t('silver')}</p>
                                <p><strong>{t('Minimum Bid')}:</strong> {(selectedListing.price + 1).toLocaleString()} {selectedListing.currency_type === 'gold' ? t('gold') : t('silver')}</p>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Your Bid')}:</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={selectedListing.price + 1}
                                    value={bidAmount}
                                    onChange={(e) => setBidAmount(e.target.value)}
                                    placeholder={t('Enter bid amount')}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    {t('Bid must be higher than')} {selectedListing.price.toLocaleString()} {selectedListing.currency_type === 'gold' ? t('gold') : t('silver')}
                                </Form.Text>
                            </Form.Group>
                            <div className="text-center mt-3">
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    disabled={bidding || !bidAmount || parseInt(bidAmount) <= selectedListing.price}
                                    className="ar_market_search_btn"
                                >
                                    {bidding ? t('Placing Bid...') : t('Place Bid')}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default PockemonMarket;
